import { useCallback, useReducer } from 'react';
import ServerError from 'errors/ServerError';
import HandlerError from 'errors/HandlerError';
import handlerRequestCanceling from 'utils/handlerRequestCanceling';
import SM from 'services/ServiceManager';

const CREATION_DELAY = 1000;

interface EditCustomerState {
    data: any;
    error: any;
    isLoading: boolean;
    createContact?: any;
    createContactError?: any;
    createContactIsLoading?: boolean;
    contactDetails?: any;
    contactDetailsError?: any;
    contactDetailsIsLoading?: boolean;
    saveContactDetails?: any;
    saveContactDetailsError?: any;
    saveContactDetailsIsLoading?: boolean;
}

type EditCustomerAction =
    | { type: 'setData'; payload: any }
    | { type: 'setError'; payload: any }
    | { type: 'setIsLoading'; payload: boolean }
    | { type: 'setCreateContact'; payload: any }
    | { type: 'setCreateContactError'; payload: any }
    | { type: 'setCreateContactIsLoading'; payload: boolean }
    | { type: 'setContactDetails'; payload: any }
    | { type: 'setContactDetailsError'; payload: any }
    | { type: 'setContactDetailsIsLoading'; payload: boolean }
    | { type: 'setSaveContactDetails'; payload: any }
    | { type: 'setSaveContactDetailsError'; payload: any }
    | { type: 'setSaveContactDetailsIsLoading'; payload: boolean };

const initialState: EditCustomerState = {
    data: null,
    error: null,
    isLoading: false,
};

const reducer = (state: EditCustomerState = initialState, action: EditCustomerAction): EditCustomerState => {
    switch (action.type) {
        case 'setData':
            return { ...state, data: action.payload };
        case 'setError':
            return { ...state, error: action.payload };
        case 'setIsLoading':
            return { ...state, isLoading: action.payload };
        case 'setCreateContact':
            return { ...state, createContact: action.payload };
        case 'setCreateContactError':
            return { ...state, createContactError: action.payload };
        case 'setCreateContactIsLoading':
            return { ...state, createContactIsLoading: action.payload };
        case 'setContactDetails':
            return { ...state, contactDetails: action.payload };
        case 'setContactDetailsError':
            return { ...state, contactDetailsError: action.payload };
        case 'setContactDetailsIsLoading':
            return { ...state, contactDetailsIsLoading: action.payload };
        case 'setSaveContactDetails':
            return { ...state, saveContactDetails: action.payload };
        case 'setSaveContactDetailsError':
            return { ...state, saveContactDetailsError: action.payload };
        case 'setSaveContactDetailsIsLoading':
            return { ...state, saveContactDetailsIsLoading: action.payload };
        default:
            return state;
    }
};

interface UseEditCustomerOptions {
    contactId?: string | number;
}

export const useEditCustomer = (options: UseEditCustomerOptions = {}) => {
    const { contactId: contactIdOption } = options;
    const [state, dispatch] = useReducer(reducer, initialState);

    // Callbacks
    const getSchema = useCallback(async ({ data }: { data?: any } = {}) => {
        dispatch({ type: 'setError', payload: null });
        dispatch({ type: 'setIsLoading', payload: true });

        try {
            const response = await SM.middleware('postCSSchemaEditCutomer', [data]);

            dispatch({ type: 'setData', payload: response?.data });
            dispatch({ type: 'setIsLoading', payload: false });

            return response?.data;
        } catch (err: any) {
            handlerRequestCanceling(
                HandlerError({
                    setError: (val) => dispatch({ type: 'setError', payload: val }),
                    setLoading: (val) => dispatch({ type: 'setIsLoading', payload: val }),
                }),
            )(err);
        }

        return false;
    }, []);

    const createContact = useCallback(({ data }: { data?: any } = {}) => new Promise((resolve, reject) => {
        dispatch({ type: 'setCreateContactError', payload: null });
        dispatch({ type: 'setCreateContactIsLoading', payload: true });

        SM.customClientService('createNewContact', [data]).then((response: any) => {
            setTimeout(() => {
                dispatch({ type: 'setCreateContact', payload: response?.data });
                dispatch({ type: 'setCreateContactIsLoading', payload: false });

                resolve(response?.data);
            }, CREATION_DELAY);
        }).catch((err: any) => {
            handlerRequestCanceling(
                HandlerError({
                    setError: (val) => dispatch({ type: 'setCreateContactError', payload: val }),
                    setLoading: (val) => dispatch({ type: 'setCreateContactIsLoading', payload: val }),
                }),
            )(err);

            reject(err.type !== undefined ? err : new ServerError(err));
        });
    }), []);

    const getContactDetails = useCallback(async ({ contactId = contactIdOption, data }: { contactId?: any; data?: any } = {}) => {
        dispatch({ type: 'setContactDetailsError', payload: null });
        dispatch({ type: 'setContactDetailsIsLoading', payload: true });

        try {
            const response = await SM.middleware('postCSContactDetails', [contactId, data]);

            dispatch({ type: 'setContactDetails', payload: response?.data });
            dispatch({ type: 'setContactDetailsIsLoading', payload: false });

            return response?.data;
        } catch (err: any) {
            handlerRequestCanceling(
                HandlerError({
                    setError: (val) => dispatch({ type: 'setContactDetailsError', payload: val }),
                    setLoading: (val) => dispatch({ type: 'setContactDetailsIsLoading', payload: val }),
                }),
            )(err);
        }

        return false;
    }, [contactIdOption]);

    const saveContactDetails = useCallback(async ({ contactId = contactIdOption, data }: { contactId?: any; data?: any } = {}) => {
        dispatch({ type: 'setSaveContactDetailsError', payload: null });
        dispatch({ type: 'setSaveContactDetailsIsLoading', payload: true });

        try {
            const response = await SM.clientService('patchContact', [contactId, data]);

            dispatch({ type: 'setSaveContactDetails', payload: response?.data });
            dispatch({ type: 'setSaveContactDetailsIsLoading', payload: false });

            return response?.data;
        } catch (err: any) {
            handlerRequestCanceling(
                HandlerError({
                    setError: (val) => dispatch({ type: 'setSaveContactDetailsError', payload: val }),
                    setLoading: (val) => dispatch({ type: 'setSaveContactDetailsIsLoading', payload: val }),
                }),
            )(err);
        }

        return false;
    }, [contactIdOption]);

    return {
        data: state.data,
        error: state.error,
        isLoading: state.isLoading,
        getSchema,
        createContactData: state.createContact,
        createContactError: state.createContactError,
        createContactIsLoading: state.createContactIsLoading,
        createContact,
        contactDetails: state.contactDetails,
        contactDetailsError: state.contactDetailsError,
        contactDetailsIsLoading: state.contactDetailsIsLoading,
        getContactDetails,
        saveDetails: state.saveContactDetails,
        saveContactDetailsError: state.saveContactDetailsError,
        saveContactDetailsIsLoading: state.saveContactDetailsIsLoading,
        saveContactDetails,
    };
};
